import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Progress,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { FaChevronLeft, FaChevronRight, FaDownload } from "react-icons/fa";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import axios from "axios";
import { apiUrl } from "../config";

const formatChartDay = (yyyyMmDd) => {
  const [, m, d] = yyyyMmDd.split("-");
  return `${m}/${d}`;
};

// Green + gray palette aligned with BusinessDashboard charts (green.600 / gray.500).
const PIE_COLORS = [
  "#38A169", // green.600
  "#2F855A", // green.700
  "#68D391", // green.300
  "#276749", // green.800
  "#9AE6B4", // green.200
  "#48BB78", // green.500
  "#718096", // gray.500
  "#4A5568", // gray.600
  "#A0AEC0", // gray.400
  "#2D3748", // gray.700
];

const currencyFmt = (value, currency = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value || 0);

const csvEscape = (value) => {
  const s = value == null ? "" : String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

const formatCsvDate = (iso) => {
  const d = new Date(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const CampaignReporting = ({ campaignId }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!campaignId) return;
      setLoading(true);
      try {
        const idToken = sessionStorage.getItem("idToken");
        const res = await axios.get(
          `${apiUrl}/campaign/${campaignId}/reporting`,
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        setData(res.data);
      } catch (err) {
        console.error("Failed to load campaign reporting", err);
        toast({
          title: "Error",
          description: "Could not load reporting data.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [campaignId, toast]);

  const currency = data?.currency || "USD";
  const goal = Number(data?.goal) || 0;
  const totalAmountSold = Number(data?.totalAmountSold) || 0;
  const dailySales = data?.dailySales || [];
  const salesByAgent = data?.salesByAgent || [];
  const agentStats = data?.agentStats || [];
  const progress = data?.progress || [];

  const formatConversion = (supporters, views) => {
    if (!views) return "—";
    return `${((supporters / views) * 100).toFixed(1)}%`;
  };

  const progressPct = useMemo(() => {
    if (!goal) return 0;
    return Math.min(100, Math.round((totalAmountSold / goal) * 100));
  }, [goal, totalAmountSold]);

  const formatDateLabel = (value) => {
    if (!value) return null;
    const d = new Date(`${value}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const startDateLabel = useMemo(
    () => formatDateLabel(data?.startDate),
    [data?.startDate]
  );
  const endDateLabel = useMemo(
    () => formatDateLabel(data?.endDate),
    [data?.endDate]
  );

  // Bar chart shows a 30-day slice of `dailySales`. Offset 0 = most recent
  // 30 days ending today (the default). Prev button steps 30 days into the
  // past; Next steps back toward today. Clamped to the campaign's date range.
  const CHART_WINDOW_DAYS = 30;
  const [chartOffset, setChartOffset] = useState(0);
  const chartWindow = useMemo(() => {
    const total = dailySales.length;
    if (!total) return [];
    const endIdx = total - chartOffset * CHART_WINDOW_DAYS;
    const startIdx = Math.max(0, endIdx - CHART_WINDOW_DAYS);
    return dailySales.slice(startIdx, endIdx);
  }, [dailySales, chartOffset]);
  const canGoOlder =
    dailySales.length > (chartOffset + 1) * CHART_WINDOW_DAYS;
  const canGoNewer = chartOffset > 0;
  const chartTitle = useMemo(() => {
    if (chartOffset === 0) return "Sales — last 30 days";
    if (!chartWindow.length) return "Sales";
    const first = formatDateLabel(chartWindow[0].date);
    const last = formatDateLabel(chartWindow[chartWindow.length - 1].date);
    return `Sales — ${first} – ${last}`;
  }, [chartOffset, chartWindow]);

  if (loading) {
    return (
      <Flex minH="40vh" justify="center" align="center" width="100%">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <VStack align="stretch" spacing={6} width="100%">
    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} width="100%">
      {/* Sales bar chart — 30-day window, navigable across campaign range */}
      <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="md">
        <Flex
          justify="space-between"
          align="center"
          mb={4}
          gap={2}
          flexWrap="wrap"
        >
          <Heading size="md">{chartTitle}</Heading>
          <Flex gap={1}>
            <IconButton
              size="sm"
              variant="outline"
              icon={<FaChevronLeft />}
              aria-label="Previous 30 days"
              isDisabled={!canGoOlder}
              onClick={() => setChartOffset((o) => o + 1)}
            />
            <IconButton
              size="sm"
              variant="outline"
              icon={<FaChevronRight />}
              aria-label="Next 30 days"
              isDisabled={!canGoNewer}
              onClick={() => setChartOffset((o) => Math.max(0, o - 1))}
            />
          </Flex>
        </Flex>
        <Box width="100%" height={{ base: "240px", md: "300px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartWindow}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={formatChartDay}
                interval="preserveStartEnd"
              />
              <YAxis allowDecimals={false} />
              <ChartTooltip
                labelFormatter={(label) => label}
                formatter={(value) => [value, "Sales"]}
              />
              <Bar dataKey="count" fill="#38A169" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {/* Pie chart of sales by team member */}
      <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="md">
        <Flex
          justify="space-between"
          align="center"
          mb={4}
          gap={3}
          flexWrap="wrap"
        >
          <Heading size="md">Sales by Team Member</Heading>
          {agentStats.length ? (
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FaDownload />}
              onClick={() => {
                const header = [
                  "Team Member",
                  "Link Views",
                  "Supporters",
                  "Conversion Rate",
                  "Sales Amount",
                ];
                const rows = agentStats.map((a) => [
                  a.name || "",
                  a.viewCount ?? 0,
                  a.saleCount ?? 0,
                  formatConversion(a.saleCount ?? 0, a.viewCount ?? 0),
                  a.saleAmount ?? 0,
                ]);
                const csv = [header, ...rows]
                  .map((row) => row.map(csvEscape).join(","))
                  .join("\r\n");
                const blob = new Blob([csv], {
                  type: "text/csv;charset=utf-8;",
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `sales-by-team-member-${campaignId}-${formatCsvDate(
                  new Date().toISOString()
                )}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              }}
            >
              Download
            </Button>
          ) : null}
        </Flex>
        {salesByAgent.length === 0 ? (
          <Flex
            align="center"
            justify="center"
            height={{ base: "240px", md: "300px" }}
          >
            <Text color="gray.500">No sales yet.</Text>
          </Flex>
        ) : (
          <Box width="100%" height={{ base: "240px", md: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={salesByAgent}
                  dataKey="saleAmount"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius="80%"
                  label={(entry) =>
                    `${entry.name}: ${currencyFmt(entry.saleAmount, currency)}`
                  }
                >
                  {salesByAgent.map((_, idx) => (
                    <Cell
                      key={idx}
                      fill={PIE_COLORS[idx % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <ChartTooltip
                  formatter={(value) => currencyFmt(value, currency)}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>

      {/* Progress towards goal */}
      <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="md">
        <Heading size="md" mb={4}>
          {goal > 0
            ? `Progress Towards Goal (${currencyFmt(goal, currency)})`
            : "Progress Towards Goal"}
        </Heading>
        {goal <= 0 ? (
          <Flex
            align="center"
            justify="center"
            height={{ base: "180px", md: "220px" }}
            direction="column"
            textAlign="center"
            px={4}
          >
            <Text color="gray.600">
              No campaign goal set. Add a Campaign Goal in Edit Campaign to
              track progress here.
            </Text>
          </Flex>
        ) : (
          <Box>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} mb={4}>
              <Stat>
                <StatLabel>Raised</StatLabel>
                <StatNumber fontSize="2xl">
                  {currencyFmt(totalAmountSold, currency)}
                </StatNumber>
                {startDateLabel && (
                  <StatHelpText mb={0}>Since {startDateLabel}</StatHelpText>
                )}
              </Stat>
              <Stat textAlign="right">
                <StatLabel>Remaining</StatLabel>
                <StatNumber fontSize="2xl">
                  {currencyFmt(
                    Math.max(goal - totalAmountSold, 0),
                    currency
                  )}
                </StatNumber>
                {endDateLabel && (
                  <StatHelpText mb={0}>Until {endDateLabel}</StatHelpText>
                )}
              </Stat>
            </SimpleGrid>
            <Progress
              value={progressPct}
              size="lg"
              borderRadius="md"
              colorScheme="green"
              hasStripe={progressPct < 100}
              isAnimated={progressPct < 100}
              sx={{ "& > div": { backgroundColor: "#38A169" } }}
            />
            <Text mt={2} fontSize="sm" color="gray.600" textAlign="right">
              {progressPct}% of goal
            </Text>
          </Box>
        )}
      </Box>

      {/* Weekly progress toward goal */}
      <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="md">
        <Heading size="md" mb={4}>
          Weekly Progress toward Goal
        </Heading>
        {progress.length === 0 ? (
          <Flex
            align="center"
            justify="center"
            height={{ base: "240px", md: "300px" }}
            direction="column"
            textAlign="center"
            px={4}
          >
            <Text color="gray.600">
              Set Campaign Start Date, End Date and Goal in Edit Campaign to
              see a progress chart.
            </Text>
          </Flex>
        ) : (
          <Box width="100%" height={{ base: "240px", md: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={progress}
                margin={{ top: 5, right: 10, bottom: 5, left: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatChartDay}
                  interval="preserveStartEnd"
                  tick={{ fontSize: 10 }}
                />
                <YAxis
                  tickFormatter={(v) => currencyFmt(v, currency)}
                  width={90}
                  tick={{ fontSize: 10 }}
                />
                <ChartTooltip
                  formatter={(value, name) => [
                    value === null || value === undefined
                      ? "—"
                      : currencyFmt(value, currency),
                    name,
                  ]}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="ideal"
                  name="On pace"
                  stroke="#A0AEC0"
                  strokeDasharray="5 5"
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="actual"
                  name="Actual"
                  stroke="#38A169"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>
    </SimpleGrid>

      {/* Per-agent stats table — full width so columns aren't cramped */}
      <Box
        bg="white"
        p={{ base: 4, md: 6 }}
        borderRadius="lg"
        boxShadow="md"
        overflowX="auto"
      >
        <Heading size="md" mb={4}>
          Team Member Performance
        </Heading>
        {agentStats.length === 0 ? (
          <Text color="gray.500">
            No team member activity yet. Sales and link views will appear here
            once team members share their links.
          </Text>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Team Member</Th>
                <Th textAlign="center">Link Views</Th>
                <Th textAlign="center">Supporters</Th>
                <Th textAlign="center">Conversion</Th>
                <Th textAlign="center">Sales Amount</Th>
              </Tr>
            </Thead>
            <Tbody>
              {agentStats.map((a) => (
                <Tr key={a.agentId}>
                  <Td>{a.name}</Td>
                  <Td textAlign="center">{a.viewCount ?? 0}</Td>
                  <Td textAlign="center">{a.saleCount ?? 0}</Td>
                  <Td textAlign="center">
                    {formatConversion(a.saleCount ?? 0, a.viewCount ?? 0)}
                  </Td>
                  <Td textAlign="center">
                    {currencyFmt(a.saleAmount ?? 0, currency)}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </VStack>
  );
};

export default CampaignReporting;
